import { ALL_PERMISSIONS, PERMISSION_LABELS, PERMISSION_TREE, fieldPermission, parsePermission, type PermissionSection, type PermissionVerb } from "@starsach/shared-types";
import { sentence } from "../components/ui";

/**
 * The permission a verb on a kind of field needs. A kind whose row has no
 * create or delete of its own (voice: view and edit only) is covered by
 * its edit, since `fieldPermission` would otherwise name one the tree
 * does not have.
 */
export function fieldVerb(fieldType: string, verb: "view" | "edit" | "create" | "delete"): string {
  const wanted = fieldPermission(fieldType, verb);
  return ALL_PERMISSIONS.includes(wanted) ? wanted : fieldPermission(fieldType, "edit");
}

/** The verbs as the matrix's columns, in the order ADR-0017 section 6 names them. */
export const VERB_COLUMNS: readonly PermissionVerb[] = ["view", "edit", "create", "delete", "approve", "request", "inspect", "undo"];

/** A section, thing or verb as the screen shows it - the register's lower-case label through `sentence()`. */
export function label(key: string): string {
  return sentence(PERMISSION_LABELS[key] ?? key.replace(/_/g, " "));
}

/** "edit on hours and holidays" - the phrase a refusal or a disabled control names. */
export function permissionPhrase(permission: string): string {
  const { section, thing, verb } = parsePermission(permission);
  const what = PERMISSION_LABELS[thing ? `${section}.${thing}` : section] ?? permission;
  return `${PERMISSION_LABELS[verb] ?? verb} on ${what}`;
}

/** The matrix's rows for one section: the section's own verbs, then each thing with its verbs. */
export function sectionRows(section: PermissionSection): Array<{ key: string; label: string; verbs: readonly string[] }> {
  const node = PERMISSION_TREE[section] as { verbs: readonly string[]; things: Record<string, readonly string[]> };
  const own = node.verbs.filter((v) => v !== "enabled");
  const rows: Array<{ key: string; label: string; verbs: readonly string[] }> = own.length > 0 ? [{ key: section, label: label(section), verbs: own }] : [];
  for (const [thing, verbs] of Object.entries(node.things)) rows.push({ key: `${section}.${thing}`, label: label(`${section}.${thing}`), verbs });
  return rows;
}

export const SECTIONS = Object.keys(PERMISSION_TREE) as PermissionSection[];
